import { BaseHttpService } from "./BaseHttpService";
import { AxiosRequestHeaders } from "axios";
import { ISessionInfo } from "services/types";
import sessionService from "services/session/sessionService";

interface ICommonLoginParams {
  username: string;
  password: string;
}

export interface ILoginWithCaptchaParams extends ICommonLoginParams {
  captcha: string;
}

export interface ILoginWithCodeParams extends ICommonLoginParams {
  validation_code: string;
  validation_token: string;
}

type ILoginParams<Type> = Type extends ILoginWithCaptchaParams
  ? ILoginWithCaptchaParams
  : Type extends ILoginWithCodeParams
    ? ILoginWithCodeParams
    : never;

export interface IAuthResult {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export interface ISignupParams {
  username: string;
  email: string;
  password: string;
  birthday: number;
  validation_code: string;
  validation_token: string;
  preferences: string;
  niches: string;
  tags: string;
}

export default class AuthService extends BaseHttpService {
  private refreshPromise: Promise<IAuthResult> | null = null;

  public constructor(readonly url?: string) {
    super(url);
  }

  public async login<T>(params: ILoginParams<T>): Promise<IAuthResult> {
    const session = await sessionService.getSession();

    return this.post("/v2/auth0/login", params, {
      "Authorization": `Bearer ${session.token}`
    });
  }

  public async refresh(params: { refresh_token: string }): Promise<IAuthResult> {
    if (this.refreshPromise === null) {
      this.refreshPromise = this.post("/v2/auth0/refresh-token", params);
    }

    const result = await this.refreshPromise;
    this.refreshPromise = null;
    return result;
  }

  public getSession(sessionId?: string | null): Promise<ISessionInfo> {
    const query = new URLSearchParams();

    if (sessionId) {
      query.append("session_id", sessionId);
    }

    return this.get(`v2/auth/temporary${query.toString().length ? `?${query.toString()}` : ""}`);
  }
}
